import React, { useCallback, useState, useMemo } from 'react';
import { ChevronLeft, Download, Info } from 'lucide-react';

/*
 * Sales Rep Performance display (P1d, management-only). Pure presentation over the
 * aggregate payload from reports:reps:performance:run — per-rep counts/%/$ only,
 * rep identities are staff (the point), no patient data. Sortable rep table +
 * totals row, tracking-start note (from P1b), unmapped footnote, client-side CSV.
 */

const card = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #DEE2E6', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#495057' };
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString('en-US');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

// Column definitions: milestone counts + revenue + conversions.
const COLS = [
  { key: 'leads', label: 'Leads' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'consultScheduled', label: 'Consults sched.' },
  { key: 'consultCompleted', label: 'Consults done' },
  { key: 'deposits', label: 'Deposits' },
  { key: 'therapies', label: 'Therapies' },
  { key: 'revenue', label: 'Booked revenue', money: true },
  { key: 'leadToDeposit', label: 'Lead→Deposit', pct: true },
  { key: 'leadToTherapy', label: 'Lead→Therapy', pct: true },
];

export default function RepPerformance({ data, onBack }) {
  const [sortKey, setSortKey] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');

  const reps = useMemo(() => {
    const rows = [...(data?.reps || [])];
    rows.sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      const cmp = typeof av === 'string' ? String(av).localeCompare(String(bv)) : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const setSort = useCallback((key) => {
    setSortKey((prev) => { if (prev === key) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return prev; } setSortDir('desc'); return key; });
  }, []);

  const exportCsv = useCallback(() => {
    if (!data) return;
    const q = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const fmt = (r, c) => (c.money ? money(r[c.key]) : c.pct ? `${r[c.key]}%` : r[c.key]);
    const header = ['Rep', ...COLS.map((c) => c.label)];
    const lines = [header.map(q).join(',')];
    for (const r of reps) lines.push([r.ownerName, ...COLS.map((c) => fmt(r, c))].map(q).join(','));
    lines.push([data.totals.ownerName, ...COLS.map((c) => fmt(data.totals, c))].map(q).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'sales_rep_performance.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }, [data, reps]);

  if (!data) return null;
  const { totals, trackingStart, approximated, unmapped } = data;
  const cell = (r, c) => (c.money ? money(r[c.key]) : c.pct ? `${r[c.key]}%` : num(r[c.key]));

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <button style={btn} onClick={onBack}><ChevronLeft size={14} /> Back</button>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#212529' }}>Sales rep performance</div>
        <span style={{ fontSize: 12, color: '#ADB5BD' }}>{fmtDate(data.range?.from)} → {data.range?.to ? fmtDate(data.range.to) : 'now'} · {reps.length} reps</span>
        <div style={{ marginLeft: 'auto' }}><button style={btn} onClick={exportCsv}><Download size={13} /> Export CSV</button></div>
      </div>

      {approximated && (
        <div style={{ ...card, background: '#FFF8E6', border: '1px solid #FDE68A', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Info size={18} color="#B45309" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: '#7C5E10', lineHeight: 1.5 }}>
            <strong>Approximation for part of this range.</strong> Progression is recorded only from{' '}
            <strong>{trackingStart ? fmtDate(trackingStart) : '(no live transitions recorded yet)'}</strong> onward.
            Leads created before then show their current stage as their furthest milestone, understating pre-recording drop-off.
          </div>
        </div>
      )}

      <div style={{ ...card, marginBottom: 12, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
          <thead>
            <tr style={{ textAlign: 'right', color: '#6C757D', fontSize: 12 }}>
              <th style={{ padding: '6px 8px', textAlign: 'left', cursor: 'pointer' }} onClick={() => setSort('ownerName')}>Rep</th>
              {COLS.map((c) => (
                <th key={c.key} style={{ padding: '6px 8px', cursor: 'pointer' }} onClick={() => setSort(c.key)}>
                  {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reps.map((r) => (
              <tr key={r.ownerId == null ? 'unassigned' : r.ownerId} style={{ borderTop: '1px solid #F1F3F5', textAlign: 'right' }}>
                <td style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#212529' }}>{r.ownerName}</td>
                {COLS.map((c) => <td key={c.key} style={{ padding: '8px' }}>{cell(r, c)}</td>)}
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #DEE2E6', textAlign: 'right', fontWeight: 700, background: '#F8F9FA' }}>
              <td style={{ padding: '8px', textAlign: 'left' }}>{totals.ownerName}</td>
              {COLS.map((c) => <td key={c.key} style={{ padding: '8px' }}>{cell(totals, c)}</td>)}
            </tr>
          </tbody>
        </table>
      </div>

      {unmapped?.count > 0 && (
        <div style={{ ...card, background: '#FDECEA', border: '1px solid #F5C6C0', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Info size={18} color="#C0392B" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: '#7B241C', lineHeight: 1.5 }}>
            <strong>{num(unmapped.count)} lead{unmapped.count === 1 ? '' : 's'} unmapped</strong> — current stage has no
            funnel-bucket mapping (still counted under each owner's Leads):{' '}
            {unmapped.stages.map((s, i) => (<span key={s.stage}>{i > 0 ? ', ' : ''}<code>{s.stage}</code> ({s.count})</span>))}.
          </div>
        </div>
      )}
    </div>
  );
}
